import { useCallback, useEffect, useState } from 'react';
import { haApi } from '../../lib/api';
import { useAppStore } from '../../stores/appStore';

interface Camera {
  entity_id: string;
  name: string;
  state: string;
  snapshot_path: string;
}

export function BlinkWidget() {
  const user = useAppStore((s) => s.user);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [alarm, setAlarm] = useState<{ state: string } | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [message, setMessage] = useState('');
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    haApi
      .blink()
      .then((res) => {
        setConfigured(Boolean(res.configured));
        if (res.cameras) setCameras(res.cameras as Camera[]);
        if (res.alarm) setAlarm(res.alarm as { state: string });
        if (res.message) setMessage(String(res.message));
        if (res.error) setMessage(String(res.error));
        setTick((t) => t + 1);
      })
      .catch((e) => setMessage(e instanceof Error ? e.message : 'Error'));
  }, []);

  useEffect(() => {
    load();
    // Snapshots refresh every 2 minutes (Blink rate limits apply)
    const t = setInterval(load, 120_000);
    return () => clearInterval(t);
  }, [load]);

  async function setMode(mode: 'arm_away' | 'arm_home' | 'disarm') {
    if (!user) return;
    try {
      await haApi.arm(mode);
      setTimeout(load, 800);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Arm failed');
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">
          Blink cameras
        </h3>
        {alarm && (
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
              alarm.state === 'disarmed'
                ? 'bg-emerald-500/20 text-emerald-300'
                : 'bg-red-500/20 text-red-300'
            }`}
          >
            {alarm.state}
          </span>
        )}
      </div>

      {configured === false && (
        <p className="text-sm text-slate-500">
          Add Blink via Home Assistant, then set HA_BLINK_CAMERAS in .env.
          Snapshots are preferred over live video for reliability.
        </p>
      )}

      {message && configured !== false && (
        <p className="mb-1 text-xs text-slate-500">{message}</p>
      )}

      <div className="grid flex-1 grid-cols-2 gap-2 overflow-auto">
        {cameras.map((cam) => (
          <div
            key={cam.entity_id}
            className="overflow-hidden rounded-lg border border-slate-700 bg-black/40"
          >
            <img
              src={`${cam.snapshot_path}?t=${tick}`}
              alt={cam.name}
              className="aspect-video w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <div className="truncate px-1.5 py-1 text-[10px] text-slate-400">
              {cam.name}
            </div>
          </div>
        ))}
      </div>

      {configured && user && (
        <div className="mt-2 flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => void setMode('arm_away')}
            className="rounded-lg bg-red-500/20 px-2 py-1 text-xs font-semibold text-red-300"
          >
            Arm away
          </button>
          <button
            type="button"
            onClick={() => void setMode('arm_home')}
            className="rounded-lg bg-amber-500/20 px-2 py-1 text-xs font-semibold text-amber-300"
          >
            Arm home
          </button>
          <button
            type="button"
            onClick={() => void setMode('disarm')}
            className="rounded-lg bg-emerald-500/20 px-2 py-1 text-xs font-semibold text-emerald-300"
          >
            Disarm
          </button>
          <button
            type="button"
            onClick={load}
            className="rounded-lg bg-slate-700 px-2 py-1 text-xs text-slate-300"
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
}
